import type { FunctionComponent, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Events, scroller } from "react-scroll";

type ScrollerOption = {
  containerId?: string;
  duration: number;
  offset?: number;
  smooth: boolean;
};

type ContextValue = {
  handleScrollTo?: (entityId: string, option: ScrollerOption) => void;
  handleUpdateScrollState?: (state: ScrollState) => void;
  scrollState?: ScrollState;
};

type ScrollState = {
  highlightedEntityId?: string;
  isScrolling?: boolean;
  toScrollEntityId?: string;
};

type Props = {
  children: ReactNode;
};

const SeriesArticleContext = createContext<ContextValue>({});

export const SeriesArticleContextProvider: FunctionComponent<Props> = ({ children }) => {
  const [scrollState, setScrollState] = useState<ScrollState>();

  const handleUpdateScrollState = useCallback((state: ScrollState) => {
    setScrollState(previous => {
      if (previous?.isScrolling) return previous;
      return { ...previous, ...state };
    });
  }, []);

  const handleScrollTo = useCallback((entityId: string, option: ScrollerOption) => {
    setScrollState({
      highlightedEntityId: entityId,
      isScrolling: true,
      toScrollEntityId: entityId,
    });
    scroller.scrollTo(`series-item-${entityId}`, option);
  }, []);

  useEffect(() => {
    Events.scrollEvent.register("end", () => {
      setScrollState(previous => ({ ...previous, isScrolling: false }));
    });
    return () => {
      Events.scrollEvent.remove("end");
    };
  }, [setScrollState]);

  return (
    <SeriesArticleContext.Provider
      value={{
        handleScrollTo,
        handleUpdateScrollState,
        scrollState,
      }}
    >
      {children}
    </SeriesArticleContext.Provider>
  );
};

export const useSeriesArticleContext = () => useContext(SeriesArticleContext);
